import { useEffect, useState } from "react";
import { X, Save, Loader2, KeyRound, Trash2, ShieldAlert, ShieldCheck, Eye, AlertTriangle, Copy, Phone, Mail } from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import api, { formatPrice } from "../lib/api";

export default function UserDetailModal({ userId, onClose, onChanged }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [tempPwd, setTempPwd] = useState(null);
  const [form, setForm] = useState({ full_name: "", phone: "", role: "user" });

  const refresh = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/users/${userId}`);
      setUser(data);
      setForm({
        full_name: data.full_name || "",
        phone: data.phone || "",
        role: data.role || "user",
      });
    } catch (e) {
      toast.error("Failed to load user");
      onClose();
    } finally { setLoading(false); }
  };

  useEffect(() => { refresh(); }, [userId]);

  const saveProfile = async () => {
    setSaving(true);
    try {
      await api.patch(`/admin/users/${userId}`, form);
      toast.success("Profile updated");
      await refresh();
      onChanged?.();
    } catch (e) {
      toast.error("Save failed: " + (e.response?.data?.detail || e.message));
    } finally { setSaving(false); }
  };

  const setStatus = async (status) => {
    if (!window.confirm(`Set status to "${status}"?`)) return;
    setSaving(true);
    try {
      await api.patch(`/admin/users/${userId}/status`, { status });
      toast.success(`User ${status}`);
      await refresh();
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Action failed");
    } finally { setSaving(false); }
  };

  const resetPassword = async () => {
    if (!window.confirm("Generate a new temporary password? The user's current password will be replaced.")) return;
    setSaving(true);
    try {
      const { data } = await api.post(`/admin/users/${userId}/reset-password`);
      setTempPwd(data.temporary_password);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Reset failed");
    } finally { setSaving(false); }
  };

  const deleteUser = async () => {
    if (!window.confirm("Permanently ban this user and hide all their listings? This cannot be easily undone.")) return;
    setSaving(true);
    try {
      await api.delete(`/admin/users/${userId}`);
      toast.success("User deleted");
      onChanged?.();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    } finally { setSaving(false); }
  };

  if (loading || !user) {
    return (
      <Backdrop onClose={onClose}>
        <div className="bg-white rounded-2xl p-10 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>
      </Backdrop>
    );
  }

  const status = user.status || "active";
  const statusStyles = {
    active: "bg-emerald-100 text-emerald-700",
    suspended: "bg-amber-100 text-amber-700",
    banned: "bg-red-100 text-red-700",
  };

  return (
    <Backdrop onClose={onClose}>
      <div className="bg-white w-full sm:max-w-3xl max-h-[90vh] overflow-y-auto rounded-t-3xl sm:rounded-2xl fade-in-up" data-testid="user-detail-modal">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-slate-200 px-5 py-4 flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-cm-primary text-white flex items-center justify-center font-display font-extrabold text-lg flex-shrink-0">
            {user.full_name?.[0]?.toUpperCase() || "?"}
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="font-display font-bold text-lg text-cm-primary truncate">{user.full_name}</h2>
            <div className="text-xs text-slate-500 truncate">{user.email}</div>
          </div>
          <span className={`text-[10px] font-semibold uppercase tracking-wider px-2 py-1 rounded-full ${statusStyles[status]}`}>
            {status}
          </span>
          <button onClick={onClose} data-testid="user-modal-close" className="w-9 h-9 rounded-full bg-slate-100 hover:bg-slate-200 flex items-center justify-center"><X className="w-4 h-4" /></button>
        </div>

        <div className="p-5 sm:p-6 space-y-6">
          {tempPwd && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
              <div className="flex items-start gap-3">
                <KeyRound className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-amber-900">Temporary password generated</div>
                  <div className="mt-1 text-xs text-amber-800">Share this securely with <strong>{user.email}</strong>. They should change it after login.</div>
                  <div className="mt-2 flex items-center gap-2">
                    <code data-testid="temp-pwd" className="flex-1 font-mono text-sm bg-white border border-amber-300 rounded px-3 py-2 select-all">{tempPwd}</code>
                    <button onClick={() => { navigator.clipboard.writeText(tempPwd); toast.success("Copied"); }} className="px-3 py-2 rounded bg-amber-600 hover:bg-amber-700 text-white text-xs font-medium flex items-center gap-1">
                      <Copy className="w-3 h-3" /> Copy
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Profile editable */}
          <Section title="Profile">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label="Full name">
                <input data-testid="user-full-name" value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} className={inputCls} />
              </Field>
              <Field label="Phone">
                <input data-testid="user-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className={inputCls} />
              </Field>
              <Field label="Email (read-only)">
                <input value={user.email} disabled className={inputCls + " opacity-60"} />
              </Field>
              <Field label="Role">
                <select data-testid="user-role" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className={inputCls} disabled={user.role === "admin"}>
                  <option value="user">User</option>
                  <option value="dealer">Dealer</option>
                  <option value="admin">Admin</option>
                </select>
              </Field>
            </div>
            <button onClick={saveProfile} disabled={saving} data-testid="save-profile-btn" className="mt-3 px-4 py-2.5 rounded-xl bg-cm-primary hover:bg-cm-accent text-white text-sm font-semibold flex items-center gap-2 disabled:opacity-60">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save profile
            </button>
          </Section>

          {/* Status actions */}
          <Section title="Account status">
            <div className="grid grid-cols-3 gap-2">
              <button onClick={() => setStatus("active")} disabled={saving || status === "active"} data-testid="status-active-btn"
                className={`px-3 py-2.5 rounded-xl text-sm font-medium border-2 ${status === "active" ? 'border-emerald-300 bg-emerald-50 text-emerald-700' : 'border-slate-200 hover:border-emerald-300 text-slate-600'} disabled:opacity-60`}>
                <ShieldCheck className="w-4 h-4 inline -mt-0.5" /> Active
              </button>
              <button onClick={() => setStatus("suspended")} disabled={saving || status === "suspended"} data-testid="status-suspended-btn"
                className={`px-3 py-2.5 rounded-xl text-sm font-medium border-2 ${status === "suspended" ? 'border-amber-300 bg-amber-50 text-amber-700' : 'border-slate-200 hover:border-amber-300 text-slate-600'} disabled:opacity-60`}>
                <ShieldAlert className="w-4 h-4 inline -mt-0.5" /> Suspend
              </button>
              <button onClick={() => setStatus("banned")} disabled={saving || status === "banned"} data-testid="status-banned-btn"
                className={`px-3 py-2.5 rounded-xl text-sm font-medium border-2 ${status === "banned" ? 'border-red-300 bg-red-50 text-red-700' : 'border-slate-200 hover:border-red-300 text-slate-600'} disabled:opacity-60`}>
                <AlertTriangle className="w-4 h-4 inline -mt-0.5" /> Ban
              </button>
            </div>
            <p className="mt-2 text-xs text-slate-500">Suspended &amp; banned users cannot sign in. Their listings are hidden from public.</p>
          </Section>

          {/* Quick actions */}
          <Section title="Actions">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <button onClick={resetPassword} disabled={saving} data-testid="reset-pwd-btn"
                className="px-4 py-2.5 rounded-xl bg-amber-500 hover:bg-amber-600 text-white text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-60">
                <KeyRound className="w-4 h-4" /> Reset password
              </button>
              <a href={`tel:${user.phone}`} className={`px-4 py-2.5 rounded-xl bg-slate-100 hover:bg-slate-200 text-cm-primary text-sm font-semibold flex items-center justify-center gap-2 ${!user.phone ? 'opacity-40 pointer-events-none' : ''}`}>
                <Phone className="w-4 h-4" /> Call user
              </a>
              <a href={`mailto:${user.email}`} className="px-4 py-2.5 rounded-xl bg-slate-100 hover:bg-slate-200 text-cm-primary text-sm font-semibold flex items-center justify-center gap-2">
                <Mail className="w-4 h-4" /> Email user
              </a>
              <button onClick={deleteUser} disabled={saving || user.role === "admin"} data-testid="delete-user-btn"
                className="px-4 py-2.5 rounded-xl bg-red-500 hover:bg-red-600 text-white text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-40">
                <Trash2 className="w-4 h-4" /> Delete user
              </button>
            </div>
          </Section>

          {/* Listings */}
          <Section title={`Listings (${user.listings?.length || 0})`}>
            {user.listings?.length ? (
              <div className="divide-y divide-slate-100 border border-slate-200 rounded-xl">
                {user.listings.slice(0, 10).map((l) => (
                  <Link key={l.id} to={`/listing/${l.id}`} target="_blank" rel="noreferrer" className="flex items-center gap-3 p-3 hover:bg-slate-50">
                    <div className="w-12 h-9 bg-slate-100 rounded overflow-hidden flex-shrink-0">
                      {l.images?.[0] && <img src={l.images[0]} alt="" className="w-full h-full object-cover" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-cm-primary truncate">{l.year} {l.brand} {l.model}</div>
                      <div className="text-xs text-slate-500">{l.location} · {l.listing_type === "rental" ? `$${l.daily_price}/day` : formatPrice(l.price)}</div>
                    </div>
                    <span className={`text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full ${l.status === "approved" ? "bg-emerald-100 text-emerald-700" : l.status === "pending" ? "bg-amber-100 text-amber-700" : "bg-red-100 text-red-700"}`}>{l.status}</span>
                    {l.suspended && <span className="text-[10px] font-semibold uppercase px-2 py-0.5 rounded-full bg-red-100 text-red-700">hidden</span>}
                  </Link>
                ))}
              </div>
            ) : <p className="text-sm text-slate-500">No listings.</p>}
          </Section>

          {/* Enquiries received */}
          <Section title={`Enquiries received (${user.enquiries?.length || 0})`}>
            {user.enquiries?.length ? (
              <div className="space-y-2">
                {user.enquiries.slice(0, 5).map((e) => (
                  <div key={e.id} className="border border-slate-200 rounded-xl p-3">
                    <div className="text-sm"><strong className="text-cm-primary">{e.user_name}</strong> <span className="text-xs text-slate-500">· {e.phone}</span></div>
                    <p className="text-xs text-slate-600 mt-1">{e.message}</p>
                  </div>
                ))}
              </div>
            ) : <p className="text-sm text-slate-500">No enquiries.</p>}
          </Section>
        </div>
      </div>
    </Backdrop>
  );
}

function Backdrop({ children, onClose }) {
  return (
    <div className="fixed inset-0 z-[60] bg-black/50 flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={(e) => e.target === e.currentTarget && onClose()}>
      {children}
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <h3 className="font-display font-bold text-cm-primary mb-2.5">{title}</h3>
      {children}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-1.5 font-semibold">{label}</span>
      {children}
    </label>
  );
}

const inputCls = "w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-2.5 text-sm focus:bg-white focus:border-cm-accent outline-none";
